import { Divider, Stack, Typography } from "@mui/material"
import { ReactElement } from "react"
import { useNavigate } from "react-router-dom"

import ExistingDeviceCard from "./existingDeviceCard"
import {
    ExistingDeviceClasses,
    LCTOnlyDatasets,
} from "../../../constants/enums"
import {
    SINGLE_DEMAND_DEVICE_NAV_LINK,
    SINGLE_GEN_DEVICE_NAV_LINK,
} from "../../../router/routes"
import { ExistingDevice } from "../../../services/middleware/mappers/dnoInterface/dnoApplicationsMapper"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import { formatKeys } from "../singleApplicationKeys"

const NON_REG_DEVICE_NOTE =
    "This device is not registered on ENA's asset registers and hence information regarding it may be limited"

interface IExistingDevicesProps {
    existingDevices: ExistingDevice[]
}

/**
 * Displays existing devices on the single application summary page. There are
 * multiple classes of existing device. This component aims to cater for
 * all of these classes, by displaying all API supplied information.
 */
const ExistingDevices = ({
    existingDevices,
}: IExistingDevicesProps): ReactElement => {
    const navigate = useNavigate()

    if (existingDevices.length === 0) {
        // If no existing devices
        return (
            <Typography>
                No existing low-carbon devices at this property
            </Typography>
        )
    }
    return (
        <Stack spacing={GRID_SPACING - 1} divider={<Divider />}>
            {existingDevices.map((device: ExistingDevice, idx: number) => {
                const index = idx + 1
                const {
                    deviceRef,
                    deviceClass,
                    deviceType,
                    deviceSysRef,
                    enaLctDataset,
                    ...details
                } = device
                if (
                    deviceClass === ExistingDeviceClasses.existingGenRegistered
                ) {
                    return (
                        <ExistingDeviceCard
                            key={deviceClass + deviceSysRef}
                            deviceReference={String(deviceSysRef)}
                            deviceType={String(deviceType)}
                            onClick={() =>
                                navigate(
                                    SINGLE_GEN_DEVICE_NAV_LINK(
                                        String(deviceSysRef)
                                    )
                                )
                            }
                            details={formatKeys(details)}
                            index={index}
                        />
                    )
                }
                if (
                    deviceClass ===
                    ExistingDeviceClasses.existingDemandRegistered
                ) {
                    return (
                        <ExistingDeviceCard
                            key={deviceClass + deviceSysRef + enaLctDataset}
                            deviceReference={String(deviceSysRef)}
                            deviceType={String(deviceType)}
                            onClick={() =>
                                navigate(
                                    SINGLE_DEMAND_DEVICE_NAV_LINK(
                                        enaLctDataset as LCTOnlyDatasets,
                                        String(deviceSysRef)
                                    )
                                )
                            }
                            details={formatKeys(details)}
                            index={index}
                        />
                    )
                }
                return (
                    <ExistingDeviceCard
                        key={`${deviceClass}${deviceRef}${index}`}
                        deviceReference={
                            deviceRef
                                ? String(deviceRef)
                                : "Device Reference Not Supplied"
                        }
                        deviceType={String(deviceType)}
                        details={{
                            ...formatKeys(details),
                            Note: NON_REG_DEVICE_NOTE,
                        }}
                        index={index}
                    />
                )
            })}
        </Stack>
    )
}
export default ExistingDevices
